import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
    X, Mail, Linkedin, Phone, Workflow, Loader2, RefreshCw,
    Copy, ExternalLink, Check, User, Building2, MessageSquare
} from 'lucide-react';
import { Button } from './ui/button';
import DISCBadge from './DISCBadge';
import CampaignProgressTracker from './CampaignProgressTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBTYPE_CONFIG = {
    email: { icon: Mail, label: 'Email', color: 'text-blue-600', bg: 'bg-blue-50', actionLabel: 'Open in Gmail & Mark Complete' },
    linkedin: { icon: Linkedin, label: 'LinkedIn', color: 'text-sky-600', bg: 'bg-sky-50', actionLabel: 'Copy Message & Mark Complete' },
    call: { icon: Phone, label: 'Call', color: 'text-emerald-600', bg: 'bg-emerald-50', actionLabel: 'Mark Complete & Advance Campaign' },
    general: { icon: Workflow, label: 'Task', color: 'text-stone-600', bg: 'bg-stone-50', actionLabel: 'Mark Complete' },
};

export default function CampaignTaskExecutionModal({ isOpen, onClose, task, onComplete }) {
    const { getAuthHeader } = useAuth();
    const [contact, setContact] = useState(null);
    const [account, setAccount] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [aiContent, setAiContent] = useState(null);
    const [editedSubject, setEditedSubject] = useState('');
    const [editedBody, setEditedBody] = useState('');

    const subtype = task?.task_subtype || 'general';
    const config = SUBTYPE_CONFIG[subtype] || SUBTYPE_CONFIG.general;
    const SubtypeIcon = config.icon;

    useEffect(() => {
        if (isOpen && task) {
            loadContext();
            if (task.ai_content) {
                setAiContent(task.ai_content);
                setEditedSubject(task.ai_content.subject || '');
                setEditedBody(task.ai_content.body || '');
            }
        }
    }, [isOpen, task]);

    const loadContext = async () => {
        setLoading(true);
        try {
            const headers = getAuthHeader();
            const requests = [];

            if (task.contact_id) {
                requests.push(
                    axios.get(`${API}/contacts/${task.contact_id}`, { headers }).catch(() => ({ data: null }))
                );
                requests.push(
                    axios.get(`${API}/contacts/${task.contact_id}/timeline?page_size=5`, { headers }).catch(() => ({ data: { data: [] } }))
                );
            } else {
                requests.push(Promise.resolve({ data: null }));
                requests.push(Promise.resolve({ data: { data: [] } }));
            }

            if (task.account_id) {
                requests.push(
                    axios.get(`${API}/accounts/${task.account_id}`, { headers }).catch(() => ({ data: null }))
                );
            } else {
                requests.push(Promise.resolve({ data: null }));
            }

            const [contactRes, timelineRes, accountRes] = await Promise.all(requests);
            setContact(contactRes.data);
            setTimeline(Array.isArray(timelineRes.data) ? timelineRes.data : timelineRes.data?.data || []);
            setAccount(accountRes.data);
        } catch (err) {
            console.error('Failed to load task context', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerate = async () => {
        setRegenerating(true);
        try {
            const res = await axios.post(
                `${API}/activities/${task.activity_id}/regenerate-content`,
                {},
                { headers: getAuthHeader() }
            );
            const newContent = res.data?.ai_content;
            if (newContent) {
                setAiContent(newContent);
                setEditedSubject(newContent.subject || '');
                setEditedBody(newContent.body || '');
                toast.success('Content regenerated');
            }
        } catch (err) {
            toast.error('Failed to regenerate content');
        } finally {
            setRegenerating(false);
        }
    };

    const handleComplete = async () => {
        setCompleting(true);
        try {
            // For email: open Gmail compose with pre-filled content
            if (subtype === 'email' && contact?.email) {
                const subject = encodeURIComponent(editedSubject || '');
                const body = encodeURIComponent((editedBody || '').replace(/<[^>]*>/g, '\n').replace(/\n{3,}/g, '\n\n').trim());
                window.open(`https://mail.google.com/mail/?view=cm&to=${contact.email}&su=${subject}&body=${body}`, '_blank');
            }

            // For LinkedIn: copy message to clipboard
            if (subtype === 'linkedin') {
                const text = editedBody || aiContent?.body || '';
                await navigator.clipboard.writeText(text);
                toast.success('Message copied to clipboard');
            }

            // Mark the task complete (this also advances the campaign via backend)
            await axios.put(
                `${API}/activities/${task.activity_id}/complete`,
                {},
                { headers: getAuthHeader() }
            );

            toast.success('Task completed — campaign advancing');
            if (onComplete) onComplete();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to complete task');
        } finally {
            setCompleting(false);
        }
    };

    const handleCopyContent = async () => {
        const text = editedBody || aiContent?.body || '';
        const clean = text.replace(/<[^>]*>/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
        await navigator.clipboard.writeText(clean);
        toast.success('Content copied to clipboard');
    };

    if (!isOpen || !task) return null;

    const contactName = contact
        ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email
        : task.contact_name || 'Contact';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden animate-fade-in flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center`}>
                            <SubtypeIcon className={`w-5 h-5 ${config.color}`} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {task.title || task.subject || 'Campaign Task'}
                            </h2>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.color} font-medium`}>
                                    {config.label}
                                </span>
                                {task.campaign_id && (
                                    <span className="text-xs text-stone-400 flex items-center gap-1">
                                        <Workflow className="w-3 h-3" /> Campaign Task
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-stone-500" />
                    </button>
                </div>

                {/* Campaign Progress */}
                {task.contact_id && (
                    <div className="px-6 py-3 border-b border-stone-100 bg-stone-50/50 shrink-0">
                        <CampaignProgressTracker contactId={task.contact_id} compact />
                    </div>
                )}

                {/* Main content — two columns */}
                <div className="flex-1 overflow-y-auto">
                    <div className="flex flex-col lg:flex-row min-h-0">
                        {/* Left: Content */}
                        <div className="flex-1 p-6 border-r border-stone-100">
                            {!aiContent && subtype !== 'general' ? (
                                <div className="text-center py-12">
                                    <SubtypeIcon className="w-10 h-10 text-stone-200 mx-auto mb-3" />
                                    <p className="text-sm text-stone-500">No AI content generated for this task</p>
                                    <Button size="sm" variant="outline" className="mt-3" onClick={handleRegenerate} disabled={regenerating}>
                                        {regenerating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                                        Generate Content
                                    </Button>
                                </div>
                            ) : subtype === 'general' ? (
                                <div>
                                    <h3 className="text-sm font-medium text-stone-700 mb-2">Task Description</h3>
                                    <p className="text-sm text-stone-600 whitespace-pre-wrap">{task.description || 'No description'}</p>
                                </div>
                            ) : (
                                <div>
                                    {/* Subject / Title */}
                                    {(subtype === 'email' || subtype === 'linkedin') && (
                                        <div className="mb-4">
                                            <label className="block text-xs font-medium text-stone-500 mb-1">
                                                {subtype === 'email' ? 'Subject' : 'Message Title'}
                                            </label>
                                            <input
                                                type="text"
                                                value={editedSubject}
                                                onChange={e => setEditedSubject(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                            />
                                        </div>
                                    )}

                                    {/* Body */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="block text-xs font-medium text-stone-500">
                                                {subtype === 'email' ? 'Email Body' : subtype === 'linkedin' ? 'Message' : 'Talking Points'}
                                            </label>
                                            <button onClick={handleCopyContent} className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1">
                                                <Copy className="w-3 h-3" /> Copy
                                            </button>
                                        </div>
                                        {subtype === 'email' ? (
                                            <div
                                                className="prose prose-sm max-w-none p-4 bg-white border border-stone-200 rounded-lg min-h-[200px]"
                                                contentEditable
                                                suppressContentEditableWarning
                                                onBlur={e => setEditedBody(DOMPurify.sanitize(e.target.innerHTML))}
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editedBody) }}
                                            />
                                        ) : (
                                            <textarea
                                                value={editedBody}
                                                onChange={e => setEditedBody(e.target.value)}
                                                rows={subtype === 'call' ? 10 : 6}
                                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-none"
                                            />
                                        )}
                                    </div>

                                    {/* Regenerate */}
                                    <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={regenerating}>
                                        {regenerating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                                        Regenerate with AI
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Right: CRM Context */}
                        <div className="w-full lg:w-80 p-6 bg-stone-50/30 shrink-0">
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                                </div>
                            ) : (
                                <div className="space-y-5">
                                    {/* Contact Card */}
                                    {contact && (
                                        <div>
                                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <User className="w-3.5 h-3.5" /> Contact
                                            </h4>
                                            <div className="bg-white rounded-lg p-3 border border-stone-100">
                                                <p className="text-sm font-medium text-stone-900">{contactName}</p>
                                                {contact.title && <p className="text-xs text-stone-500">{contact.title}</p>}
                                                {contact.email && <p className="text-xs text-stone-400 mt-1">{contact.email}</p>}
                                                {contact.disc_profile && (
                                                    <div className="mt-2">
                                                        <DISCBadge profile={contact.disc_profile} secondary={contact.disc_secondary} size="sm" />
                                                    </div>
                                                )}
                                                {contact.communication_preferences && (
                                                    <div className="mt-2 text-xs text-stone-500">
                                                        <p>Pace: {contact.communication_preferences.pace || 'moderate'}</p>
                                                        <p>Tone: {contact.communication_preferences.tone || 'professional'}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Account Card */}
                                    {account && (
                                        <div>
                                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <Building2 className="w-3.5 h-3.5" /> Company
                                            </h4>
                                            <div className="bg-white rounded-lg p-3 border border-stone-100">
                                                <p className="text-sm font-medium text-stone-900">{account.name}</p>
                                                {account.industry && <p className="text-xs text-stone-500">{account.industry}</p>}
                                                {account.size && <p className="text-xs text-stone-400">{account.size}</p>}
                                            </div>
                                        </div>
                                    )}

                                    {/* Recent Interactions */}
                                    {timeline.length > 0 && (
                                        <div>
                                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <MessageSquare className="w-3.5 h-3.5" /> Recent Activity
                                            </h4>
                                            <div className="space-y-2">
                                                {timeline.slice(0, 3).map((item, idx) => (
                                                    <div key={idx} className="bg-white rounded-lg p-2.5 border border-stone-100">
                                                        <p className="text-xs font-medium text-stone-700 truncate">
                                                            {item.subject || item.title || item.type}
                                                        </p>
                                                        <p className="text-xs text-stone-400 mt-0.5">
                                                            {item.direction || item.type} • {item.date ? new Date(item.date).toLocaleDateString() : ''}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Coaching Notes */}
                                    {aiContent?.coaching_notes?.length > 0 && (
                                        <div>
                                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">
                                                AI Coaching Notes
                                            </h4>
                                            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                                                <ul className="space-y-1.5">
                                                    {aiContent.coaching_notes.map((note, idx) => (
                                                        <li key={idx} className="text-xs text-amber-800 flex items-start gap-1.5">
                                                            <span className="text-amber-400 mt-0.5">•</span> {note}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-stone-200 bg-stone-50 shrink-0">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={handleComplete}
                        disabled={completing}
                        className="bg-stone-900 hover:bg-stone-800"
                    >
                        {completing ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Completing...</>
                        ) : (
                            <>
                                {subtype === 'email' && <ExternalLink className="w-4 h-4 mr-2" />}
                                {subtype === 'linkedin' && <Copy className="w-4 h-4 mr-2" />}
                                {(subtype === 'call' || subtype === 'general') && <Check className="w-4 h-4 mr-2" />}
                                {config.actionLabel}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
